"use client";

import React from "react";
import { ArrowRight, Star, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RedefineYourLimits() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="230560" id="redefine-your-limits" className="bg-background text-foreground relative overflow-hidden min-h-screen flex items-center">
      {}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-0 w-2/3 h-full opacity-5" style={{
        background: "radial-gradient(ellipse at 20% 50%, var(--primary) 0%, transparent 60%)"
      }} />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5" style={{
        background: "radial-gradient(ellipse at 80% 30%, var(--primary) 0%, transparent 60%)"
      }} />
        {}
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
        backgroundSize: "60px 60px"
      }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {}
          <div ref={leftRef} className={`flex flex-col gap-6 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-16"}`}>
            {}
            <div className="inline-flex items-center gap-2 w-fit px-4 py-2 rounded-md border border-border bg-card/50 backdrop-blur-sm">
              <Star className="size-3.5 fill-primary text-primary" />
              <span className="text-foreground text-xs font-default font-bold tracking-widest uppercase">Neuro-Sensory Performance Clinic</span>
            </div>

            {}
            <div className="flex flex-col gap-1">
              <h1 className="font-default font-bold leading-none tracking-tighter uppercase" style={{
              fontSize: "clamp(3rem, 8vw, 6rem)"
            }}>
                <span className="block text-foreground">Re-Train</span>
                <span className="block text-foreground">YOUR</span>
                <span className="block" style={{
                background: "linear-gradient(135deg, var(--primary) 0%, var(--primary) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}>Brain</span>
              </h1>
            </div>

            {}
            <div className="flex flex-col gap-3">
              <p className="font-default text-lg sm:text-xl text-foreground leading-relaxed max-w-md">
                Our <span className="text-primary font-bold italic">Neuro-Sensory</span> approach 
                bridges the gap between elite athletic potential and pain-free movement.
              </p>
              <p className="font-default text-sm text-muted-foreground max-w-sm">
                One-on-one treatment sessions designed for athletes and active adults in Tulsa.
              </p>
            </div>

            {}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 max-w-16" style={{
              background: "var(--primary)"
            }} />
              <div className="h-1.5 w-1.5 rounded-md" style={{
              background: "var(--primary)"
            }} />
              <div className="h-px flex-1 max-w-8" style={{
              background: "var(--border)"
            }} />
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              {}
              <div className="relative">
                {}
                <span className="absolute inset-0 rounded-md animate-ping opacity-30" style={{
                background: "var(--primary)",
                animationDuration: "2s"
              }} aria-hidden="true" />
                <span className="absolute inset-0 rounded-md animate-ping opacity-20" style={{
                background: "var(--primary)",
                animationDuration: "2s",
                animationDelay: "0.5s"
              }} aria-hidden="true" />
                <Button asChild size="lg" className="relative z-10 font-default font-bold text-base px-8 py-6 rounded-md text-primary-foreground shadow-md uppercase tracking-wider" style={{
                background: "var(--primary)"
              }}>
                  <a href="#start-your-recovery">
                    Book Assessment
                    <ArrowRight className="size-4 ml-1" />
                  </a>
                </Button>
              </div>

              <Button asChild variant="outline" size="lg" className="font-default font-bold text-base px-8 py-6 rounded-md border-foreground text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors uppercase tracking-wider">
                <a href="#path-to-peak-performance">
                  Our Method
                </a>
              </Button>
            </div>

            {}
            <div ref={statsRef} className={`flex flex-wrap gap-6 pt-4 border-t border-border transition-all duration-700 delay-300 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {[{
              value: "98%",
              label: "Pain Reduction"
            }, {
              value: "150+",
              label: "Elite Athletes"
            }, {
              value: "NEURO",
              label: "Focused Training"
            }].map((stat, i) => <div data-index={i} key={i} className="flex flex-col gap-0.5">
                  <span className="font-default font-bold text-xl uppercase" style={{
                color: "var(--foreground)"
              }}>
                    {stat.value}
                  </span>
                  <span className="font-default text-xs text-muted-foreground uppercase tracking-widest">{stat.label}</span>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`relative flex items-center justify-center transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-16"}`}>
            {}
            <div className="absolute inset-0 rounded-md opacity-10 blur-3xl" style={{
            background: "var(--primary)"
          }} aria-hidden="true" />

            {}
            <div className="relative w-full max-w-sm sm:max-w-md lg:max-w-full">
              {}
              <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md shadow-lg grayscale" style={{
              clipPath: "polygon(10% 0%, 100% 0%, 100% 90%, 90% 100%, 0% 100%, 0% 10%)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689046/img/tulsa_athlete_sprint_neuro.png" alt="Athlete in mid-sprint with neuro-sensory focus" className="w-full h-full object-cover object-center" />
                {}
                <div className="absolute inset-0" style={{
                background: "linear-gradient(180deg, transparent 60%, var(--background) 100%)"
              }} aria-hidden="true" />
              </div>

              {}
              <div className="absolute inset-0 rounded-md border-2 opacity-20 pointer-events-none" style={{
              borderColor: "var(--primary)",
              clipPath: "polygon(10% 0%, 100% 0%, 100% 90%, 90% 100%, 0% 100%, 0% 10%)"
            }} aria-hidden="true" />

              {}
              <div className="absolute top-6 -left-4 sm:-left-8 bg-background border border-border rounded-md px-4 py-3 shadow-md flex items-center gap-3">
                <div className="size-8 rounded-md flex items-center justify-center shrink-0" style={{
                background: "var(--primary)"
              }}>
                  <Users className="size-4 text-white" />
                </div>
                <div>
                  <p className="font-default font-bold text-sm text-foreground uppercase">Elite Edge</p>
                  <p className="font-default text-xs text-muted-foreground uppercase">Performance First</p>
                </div>
              </div>

              {}
              <div className="absolute bottom-16 -right-2 sm:-right-6 bg-background border border-border rounded-md px-4 py-3 shadow-md">
                <p className="font-default text-xs text-muted-foreground mb-0.5 uppercase tracking-widest">Neural Status</p>
                <p className="font-default font-bold text-base uppercase italic" style={{
                color: "var(--primary)"
              }}>
                  Optimized
                </p>
              </div>

              {}
              {["absolute top-1/4 left-1/2 -translate-x-1/2", "absolute top-1/3 left-1/2 -translate-x-1/2", "absolute top-1/2 left-1/2 -translate-x-1/2"].map((pos, i) => <div data-index={i} key={i} className={`${pos} size-2 rounded-full animate-pulse`} style={{
              background: "var(--primary)",
              animationDelay: `${i * 0.3}s`
            }} aria-hidden="true" />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}